import React from 'react';
import { CardContext, CardProviderProps } from '../hooks/useCards';
import { contentfulApiFeaturedBannerToCardProps } from './contentfulApiFeaturedBannerToCardProps';
import {
  NoResultsPlaceholder,
  NoResultsPlaceholderProps,
} from '@jbhi-fi/components/src/NoResultsPlaceholder/NoResultsPlaceholder';
import { ContentfulCardGrid, ContentfulSection, EntryType } from '@jbhi-fi/apis/src/contentful/types';
import { CardGrid, CardGridProps } from '@jbhi-fi/components/src/ContentCard';
import { CardGridCardProps } from '@jbhi-fi/components/src/ContentCard/CardGrid/CardGrid';
import { ContentfulFilter } from '@jbhi-fi/components/src/hooks/useContentfulEntries/useContentfulEntries';
import { getLocalTimeWithoutTimezone } from '@jbhi-fi/utils/src/date';

export type ContentfulApiCardGridToCardGridWithProviderPropsOptions = {
  cardGridProps?: Omit<CardGridProps, 'cards'>;
  featuredBannerCardIndex?: number;
  section?: ContentfulSection;
};

export const contentfulApiCardGridToCardGridWithProviderProps = (
  cardGrid: ContentfulCardGrid,
  options: ContentfulApiCardGridToCardGridWithProviderPropsOptions = {}
): CardProviderProps => {
  const {
    cardGridProps = options.cardGridProps ?? {},
    featuredBannerCardIndex = 5,
    section = {} as ContentfulSection,
  } = options;
  const { fields } = cardGrid;

  const hasFeaturedBanner = Boolean(fields?.featuredBanner);
  const featuredBannerProps = fields?.featuredBanner?.fields
    ? contentfulApiFeaturedBannerToCardProps(fields.featuredBanner)
    : hasFeaturedBanner
    ? { cover: <></>, isLoading: true }
    : null;

  return {
    numCards: hasFeaturedBanner ? (fields?.cardCount ?? 0) - 1 : fields?.cardCount ?? 0,
    useLoadingSkeletons: true,
    filters: fields?.parentPage?.sys?.id ? contentfulApiCardGridToContentfulFilters(cardGrid) : [],
    children: fields?.parentPage?.sys?.id ? (
      <CardContext.Consumer>
        {({ cards: cardsProp = [] }) => {
          const cards = [...cardsProp];
          if (cards.length === 0) {
            return <NoResultsPlaceholder {...getNoCardsPlaceholderProps(section.fields?.heading.toLowerCase())} />;
          }
          if (hasFeaturedBanner) {
            cards.splice(featuredBannerCardIndex, 0, featuredBannerProps as CardGridCardProps);
          }
          return (
            <CardGrid
              cards={cards.map(card => ({
                ...card,
                width: cardGridProps.isCarousel ? '345px' : undefined,
              }))}
              {...cardGridProps}
            />
          );
        }}
      </CardContext.Consumer>
    ) : null,
  };
};

export const getNoCardsPlaceholderProps = (pageTitle?: string): NoResultsPlaceholderProps => {
  if (!pageTitle) {
    return {
      icon: 'LampIllustrated',
      title: 'Nothing here... yet!',
      contents: "The lights are on but no one's home for this category. Come back soon.",
    };
  }
  return {
    icon: 'LampIllustrated',
    title: `No ${pageTitle.toLowerCase()} here... yet!`,
    contents: `The lights are on but no ${pageTitle.toLowerCase()} are home for this category. Come back soon.`,
  };
};

const contentfulApiCardGridToContentfulFilters = ({ fields }: ContentfulCardGrid): Array<ContentfulFilter> => {
  const filter: Omit<ContentfulFilter, 'content_type'> = { order: '-sys.createdAt' };

  switch (fields?.parentPage?.sys?.contentType?.sys?.id) {
    case EntryType.categoryLanding:
      filter['fields.parentPage.sys.contentType.sys.id'] = EntryType.subCategory;
      filter['fields.parentPage.fields.categoryPage.sys.id'] = fields.parentPage.sys.id;
      break;
    case EntryType.subCategory:
      filter['fields.parentPage.sys.id'] = fields.parentPage.sys.id;
      break;
  }

  if (fields && fields.fromLastXDays)
    filter['sys.updatedAt[gte]'] = new Date(Date.now() - fields.fromLastXDays * 86400000).toISOString();

  return [
    { ...filter, content_type: EntryType.competition, 'fields.endDate[gte]': getLocalTimeWithoutTimezone(new Date()) },
    { ...filter, content_type: EntryType.article, order: '-fields.publishedDate' },
    {
      ...filter,
      content_type: EntryType.genericPage,
      order: '-fields.publishedDate',
      'fields.heroLogo[exists]': 'true',
    },
  ];
};
